import { useCallback, useEffect, useState } from 'react';

import { IconButton } from './primitives.js';

/**
 * Dark mode (prototype `[data-theme]` and its header toggle). Without a choice the page follows the
 * system (`prefers-color-scheme`, handled in the token CSS); the toggle pins light or dark on
 * `<html data-theme>`. Theme and FND-25 device preferences (`@saay/web-app`) may use `localStorage`;
 * tokens and other sensitive data never do (T-01).
 */

export type ThemeName = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'saay-theme';

const THEME_COLOR: Record<ThemeName, string> = { dark: '#1c1c1e', light: '#f2f2f7' };

function readStoredTheme(): ThemeName | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): ThemeName {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function applyTheme(theme: ThemeName | null): void {
  const root = document.documentElement;
  if (theme === null) {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = theme;
  }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[theme ?? systemTheme()]);
}

/** Applies a stored theme choice before the first render (call once at startup). */
export function initTheme(): void {
  applyTheme(readStoredTheme());
}

export function useTheme(): { readonly theme: ThemeName; readonly toggle: () => void } {
  const [theme, setTheme] = useState<ThemeName>(() => readStoredTheme() ?? systemTheme());

  useEffect(() => {
    applyTheme(readStoredTheme());
  }, []);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: ThemeName = current === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Storage is unavailable (private mode): the choice lasts for this page only.
      }
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggle };
}

/** The header theme button (prototype `[data-theme-toggle]`): moon in light, sun in dark. */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <IconButton
      aria-pressed={theme === 'dark'}
      icon={theme === 'dark' ? 'sun' : 'moon'}
      label="حالت تیره"
      onClick={toggle}
    />
  );
}
